/**
 * Grades note-on events against metronome pulses (downbeats / upbeats).
 * Used by metronome-locked exercises and stage exams later.
 */

import type { MetronomeBeatEvent, MetronomeEngine } from "./MetronomeEngine.js";

export type SyncQuality = "perfect" | "good" | "early" | "late" | "miss";

export interface SyncGradeWindows {
  /** |delta| ≤ this → perfect (ms) */
  perfectMs: number;
  /** |delta| ≤ this → good (ms) */
  goodMs: number;
  /** Beyond this → miss (ms) */
  missMs: number;
}

export const DEFAULT_SYNC_WINDOWS: SyncGradeWindows = {
  perfectMs: 35,
  goodMs: 70,
  missMs: 140,
};

export interface SyncGradeResult {
  quality: SyncQuality;
  /** Signed: negative = early (before beat), positive = late. */
  deltaMs: number;
  nearestBeat: MetronomeBeatEvent | null;
  scored: boolean;
}

export interface SyncSessionStats {
  attempts: number;
  perfect: number;
  good: number;
  early: number;
  late: number;
  miss: number;
  /** Average signed delta (ms); negative = rushing, positive = dragging. */
  meanDeltaMs: number;
  /** Timing consistency — std-dev of deltas (ms). */
  jitterMs: number;
  accuracyPct: number;
}

function qualityFromDelta(absMs: number, signed: number, w: SyncGradeWindows): SyncQuality {
  if (absMs > w.missMs) return "miss";
  if (absMs <= w.perfectMs) return "perfect";
  if (absMs <= w.goodMs) return "good";
  return signed < 0 ? "early" : "late";
}

export class BeatSyncGrader {
  private windows: SyncGradeWindows;
  private deltas: number[] = [];
  private counts = {
    attempts: 0,
    perfect: 0,
    good: 0,
    early: 0,
    late: 0,
    miss: 0,
  };
  private enabled = false;

  constructor(windows: Partial<SyncGradeWindows> = {}) {
    this.windows = { ...DEFAULT_SYNC_WINDOWS, ...windows };
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setWindows(windows: Partial<SyncGradeWindows>): void {
    this.windows = { ...this.windows, ...windows };
  }

  reset(): void {
    this.deltas = [];
    this.counts = {
      attempts: 0,
      perfect: 0,
      good: 0,
      early: 0,
      late: 0,
      miss: 0,
    };
  }

  /**
   * Grade a note-on against the nearest recent metronome pulse.
   * `noteAudioTime` should be AudioContext.currentTime (or scheduled time).
   */
  grade(metronome: MetronomeEngine, noteAudioTime: number): SyncGradeResult {
    if (!this.enabled || !metronome.isRunning) {
      return {
        quality: "miss",
        deltaMs: 0,
        nearestBeat: null,
        scored: false,
      };
    }

    const candidates = metronome.getRecentBeats(noteAudioTime);
    // Also consider the mathematically next pulse if list is empty / thin
    let nearest: MetronomeBeatEvent | null = null;
    let bestDist = Infinity;

    for (const b of candidates) {
      const d = Math.abs(b.audioTime - noteAudioTime);
      if (d < bestDist) {
        bestDist = d;
        nearest = b;
      }
    }

    if (!nearest) {
      this.counts.attempts += 1;
      this.counts.miss += 1;
      return {
        quality: "miss",
        deltaMs: 0,
        nearestBeat: null,
        scored: true,
      };
    }

    const deltaMs = (noteAudioTime - nearest.audioTime) * 1000;
    const quality = qualityFromDelta(Math.abs(deltaMs), deltaMs, this.windows);

    this.counts.attempts += 1;
    switch (quality) {
      case "perfect":
        this.counts.perfect += 1;
        break;
      case "good":
        this.counts.good += 1;
        break;
      case "early":
        this.counts.early += 1;
        break;
      case "late":
        this.counts.late += 1;
        break;
      default:
        this.counts.miss += 1;
        break;
    }
    if (quality !== "miss") this.deltas.push(deltaMs);

    return {
      quality,
      deltaMs,
      nearestBeat: nearest,
      scored: true,
    };
  }

  getStats(): SyncSessionStats {
    const { attempts, perfect, good, early, late, miss } = this.counts;
    const n = this.deltas.length;
    const mean = n ? this.deltas.reduce((a, b) => a + b, 0) / n : 0;
    const variance = n
      ? this.deltas.reduce((a, d) => a + (d - mean) ** 2, 0) / n
      : 0;
    const hit = perfect + good;
    return {
      attempts,
      perfect,
      good,
      early,
      late,
      miss,
      meanDeltaMs: mean,
      jitterMs: Math.sqrt(variance),
      accuracyPct: attempts ? (hit / attempts) * 100 : 0,
    };
  }
}
